import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import {
  CloudArrowUp, CheckCircle, XCircle, ArrowsClockwise, FileArrowDown,
} from '@phosphor-icons/react';
import api from '../../services/api';
import { formatDate } from '../../lib/utils';

export const OdmSftpTab = ({ sftpLoading, sftpStatus, sftpFiles, onRefresh }) => {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tunnelHealth, setTunnelHealth] = useState(null);
  const [tunnelLoading, setTunnelLoading] = useState(false);

  const fetchTunnelHealth = async () => {
    setTunnelLoading(true);
    try {
      const res = await api.get('/odm-sftp/tunnel/health');
      setTunnelHealth(res.data);
    } catch (error) {
      // AZAI super-admin only; agency users will 403 silently.
      if (error.response?.status !== 403) {
        toast.error('Failed to load tunnel health');
      }
    } finally {
      setTunnelLoading(false);
    }
  };

  React.useEffect(() => {
    fetchTunnelHealth();
  }, []);

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.get('/odm-sftp/test-connection');
      setTestResult(res.data);
      res.data.success ? toast.success('SFTP connection successful!') : toast.error(`SFTP connection failed: ${res.data.error || 'Unknown error'}`);
    } catch (error) {
      toast.error('Failed to test SFTP connection');
      setTestResult({ success: false, error: error.response?.data?.detail || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleListFiles = async () => {
    try {
      const res = await api.get('/odm-sftp/list-inbound', { params: { file_type: null } });
      onRefresh?.(res.data.files || []);
    } catch (error) {
      toast.error('Failed to list SFTP files');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post('/odm-sftp/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Uploaded ${file.name}`);
      handleListFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (sftpLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <CloudArrowUp size={20} className="text-indigo-600" />
                Ohio Medicaid EDI SFTP
              </CardTitle>
              <CardDescription>Secure file transfer for EDI claims and responses</CardDescription>
            </div>
            <Badge className={sftpStatus?.connected ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
              {sftpStatus?.connected ? 'Connected' : 'Disconnected'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-slate-500">Host</span>
              <p className="font-mono">{sftpStatus?.host || 'mits.odjfs.state.oh.us'}</p>
            </div>
            <div>
              <span className="text-slate-500">Tunnel</span>
              <p>{sftpStatus?.tunnel_active ? 'Active (DigitalOcean)' : 'Inactive'}</p>
            </div>
            <div>
              <span className="text-slate-500">IP</span>
              <p className="font-mono">{sftpStatus?.tunnel_ip || '159.203.168.247'}</p>
            </div>
            <div>
              <span className="text-slate-500">Last Test</span>
              <p>{sftpStatus?.last_test ? formatDate(sftpStatus.last_test) : 'Never'}</p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleTestConnection} disabled={testing} data-testid="test-sftp-btn">
              {testing ? <ArrowsClockwise size={16} className="mr-1.5 animate-spin" /> : <CloudArrowUp size={16} className="mr-1.5" />}
              {testing ? 'Testing...' : 'Test Connection'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleListFiles} data-testid="list-sftp-btn">
              <FileArrowDown size={16} className="mr-1.5" />
              List Inbound Files
            </Button>
            <label className="inline-flex items-center gap-2 px-3 py-1 text-sm border rounded-md cursor-pointer hover:bg-slate-50">
              <CloudArrowUp size={16} />
              {uploading ? 'Uploading...' : 'Upload File'}
              <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
            </label>
          </div>

          {testResult && (
            <div className={`p-3 rounded-lg border ${testResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="sftp-test-result">
              <div className="flex items-center gap-2">
                {testResult.success ? <CheckCircle size={20} className="text-emerald-600" /> : <XCircle size={20} className="text-red-600" />}
                <span className="font-medium text-sm">{testResult.success ? 'Connection Successful' : 'Connection Failed'}</span>
                {testResult.mode && <Badge className="ml-1 bg-slate-100 text-slate-600 text-xs">{testResult.mode}</Badge>}
              </div>
              {testResult.error && <p className="text-xs text-red-600 mt-1" data-testid="sftp-test-error">{testResult.error}</p>}
              {testResult.security_warning && <p className="text-xs text-red-600 mt-1">{testResult.security_warning}</p>}
              {testResult.latency_ms && <p className="text-xs text-slate-500 mt-1">Latency: {testResult.latency_ms}ms</p>}

              {testResult.success && testResult.mode === 'live' && (
                <div className="mt-2 space-y-1 text-xs text-slate-700" data-testid="sftp-test-directories">
                  {testResult.host && (
                    <p><span className="text-slate-500">Host:</span> <span className="font-mono">{testResult.host}</span></p>
                  )}
                  {Array.isArray(testResult.root_directories) && (
                    <p><span className="text-slate-500">Root entries:</span> {testResult.root_directories.length}</p>
                  )}
                  {testResult.directories && ['inbound', 'outbound'].map((k) => {
                    const d = testResult.directories[k];
                    if (!d) return null;
                    return (
                      <div key={k} className="flex items-center gap-1.5" data-testid={`sftp-dir-${k}`}>
                        {d.accessible
                          ? <CheckCircle size={14} className="text-emerald-600" />
                          : <XCircle size={14} className="text-red-600" />}
                        <span className="capitalize text-slate-500">{k}:</span>
                        <span className="font-mono">{d.path}</span>
                        <span className="text-slate-500">
                          — {d.accessible ? `reachable (${d.file_count} file${d.file_count === 1 ? '' : 's'})` : 'not reachable'}
                        </span>
                      </div>
                    );
                  })}
                  <p className="text-[11px] text-slate-400 pt-1">
                    Read-only check — directory listing only; no files created, uploaded, downloaded, or transmitted.
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {tunnelHealth && (
        <Card className="border-slate-200" data-testid="tunnel-health-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  Tunnel Health
                  <Badge
                    data-testid="tunnel-health-active-badge"
                    className={
                      tunnelHealth.active === 'primary'
                        ? 'bg-emerald-100 text-emerald-700'
                        : tunnelHealth.active === 'standby'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-red-100 text-red-700'
                    }
                  >
                    {tunnelHealth.active
                      ? `Active: ${tunnelHealth.active}`
                      : 'BOTH DOWN'}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Active-passive DigitalOcean SSH tunnels for ODM SFTP. Standby is
                  promoted automatically when the primary fails its TCP probe.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchTunnelHealth}
                disabled={tunnelLoading}
                data-testid="refresh-tunnel-health-btn"
              >
                {tunnelLoading
                  ? <ArrowsClockwise size={16} className="mr-1.5 animate-spin" />
                  : <ArrowsClockwise size={16} className="mr-1.5" />}
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Endpoint</TableHead>
                  <TableHead>Host</TableHead>
                  <TableHead>Port</TableHead>
                  <TableHead>Reachable</TableHead>
                  <TableHead>Probe (ms)</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(tunnelHealth.endpoints || []).map((ep) => (
                  <TableRow key={ep.name} data-testid={`tunnel-row-${ep.name}`}>
                    <TableCell className="font-medium">
                      {ep.name}
                      {tunnelHealth.last_used === ep.name && (
                        <Badge className="ml-2 bg-indigo-100 text-indigo-700 text-xs">
                          last used
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{ep.host}</TableCell>
                    <TableCell className="text-sm">{ep.port}</TableCell>
                    <TableCell>
                      {ep.reachable
                        ? <CheckCircle size={16} className="text-emerald-600" />
                        : <XCircle size={16} className="text-red-600" />}
                    </TableCell>
                    <TableCell className="text-sm">{ep.elapsed_ms}</TableCell>
                    <TableCell className="text-xs text-red-600 max-w-[280px] truncate">
                      {ep.error || ''}
                    </TableCell>
                  </TableRow>
                ))}
                {(!tunnelHealth.endpoints || tunnelHealth.endpoints.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-slate-500">
                      No tunnel endpoints configured. Set DO_TUNNEL_HOST (and
                      optionally DO_TUNNEL_HOST_STANDBY) in the backend env.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {tunnelHealth.endpoints && tunnelHealth.endpoints.length === 1 && (
              <p className="text-xs text-amber-700 mt-3" data-testid="tunnel-no-standby-warning">
                ⚠ Only one tunnel is configured — this is a single point of failure
                for every claim submission. Provision a second DigitalOcean droplet
                and set <code className="font-mono">DO_TUNNEL_HOST_STANDBY</code>.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {sftpFiles.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Inbound Files ({sftpFiles.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File Name</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Modified</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sftpFiles.map((file, idx) => (
                  <TableRow key={file.filename || idx}>
                    <TableCell className="font-mono text-sm">{file.filename}</TableCell>
                    <TableCell className="text-sm">{file.size}</TableCell>
                    <TableCell className="text-sm">{formatDate(file.modified)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" data-testid={`download-sftp-${idx}`}>
                        <FileArrowDown size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </>
  );
};
